import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentProps } from 'react'
import { cn } from '../lib/cn'

/**
 * Maps the Figma `<Button>` component set: 4 types x 4 sizes x 3 states
 * (48 variants). See design/COMPONENTS.md.
 *
 * Hover and disabled are CSS states rather than explicit variants — the Figma
 * "State" axis exists because Figma cannot express pseudo-classes.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap ' +
    // Colour *and* transform: the press is the acknowledgement. A button that
    // only changes colour on tap feels like a picture of a button; one that
    // gives under the finger feels like it heard you. 90ms, because feedback
    // slower than that is felt as the app being slow rather than as the button
    // responding.
    'transition-[colors,transform,box-shadow,opacity] duration-[--duration-instant] ' +
    'ease-[--ease-out-soft] active:scale-[0.97] ' +
    // A disabled button must not pretend to respond.
    'disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 ' +
    // 44px minimum touch target on coarse pointers (WCAG 2.5.5 / spec §UI/UX).
    // The frames draw their primary action at exactly 44, so the floor is set
    // there rather than at 48 — otherwise every mobile screen would inflate.
    'pointer-coarse:min-h-11 ' +
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary: 'bg-brand-600 text-white hover:bg-brand-500 active:bg-brand-700',
        secondary: 'bg-slate-700 text-white hover:bg-slate-600 active:bg-slate-800',
        ghost: 'bg-transparent text-brand-600 hover:bg-brand-50 active:bg-brand-100',
        danger: 'bg-danger text-white hover:opacity-90 active:opacity-100',
        outline: 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
      },
      size: {
        xs: 'h-7 px-2.5 text-2xs',
        sm: 'h-9 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        // The mobile frames' action button: 44px tall, 16/500 label.
        lg: 'h-11 px-6 text-base',
      },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
)

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof button> & {
    /** Render as the child element (e.g. an anchor) instead of a `<button>`. */
    asChild?: boolean
  }

export function Button({
  className,
  variant,
  size,
  block,
  asChild = false,
  type = 'button',
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      // `type` is intentionally defaulted: an un-typed button inside a form
      // submits it, which is almost never what a design mock intends.
      {...(asChild ? {} : { type })}
      className={cn(button({ variant, size, block }), className)}
      {...props}
    />
  )
}

export { button as buttonVariants }
